// /admin/analytics — Director / Analytics Console.
//
// Decision-support, not BI: every card answers "what is happening,
// what is changing, what needs action, where should I click next."
// One RPC drives the whole page; risk drill-downs come from the
// existing Phase 7 helpers.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowsClockwise, ArrowUpRight, ArrowDownRight, MinusCircle,
  Users, FirstAid, ShieldCheck, Receipt, CalendarCheck, Buildings, Sparkle, Warning, Lightning,
  PaperPlaneTilt, UserCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  emailAdminAnalyticsSummary,
  fetchAdminAnalyticsOverview,
  fetchAdminAnalyticsRisks,
  deltaArrow,
  deltaTone,
} from "@/lib/adminAnalytics";
import { centsToUsd } from "@/lib/billing";
import DirectorMorningBrief from "@/components/admin/DirectorMorningBrief";

export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [risks, setRisks] = useState({ clearance: [], ratios: [] });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [o, r] = await Promise.all([
        fetchAdminAnalyticsOverview(),
        fetchAdminAnalyticsRisks(),
      ]);
      setOverview(o);
      setRisks(r || { clearance: [], ratios: [] });
    } catch (err) {
      toast.error(err.message || "Could not load analytics");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  // Module F · email-summary state.
  const [emailing, setEmailing] = useState(false);
  const onEmailSummary = async () => {
    setEmailing(true);
    try {
      const res = await emailAdminAnalyticsSummary();
      if (res.status === "delivered" || res.status === "ok") {
        toast.success(`Emailed to ${res.to_email}`);
      } else if (res.status === "skipped_idempotent") {
        toast.info(`Already sent to ${res.to_email} today`);
      } else if (res.status === "skipped_pref_off") {
        toast.info("Email preference is off for this kind");
      } else {
        toast.message(res.reason || `Send finished with status: ${res.status}`);
      }
    } catch (err) {
      toast.error(err.message || "Could not send summary");
    } finally {
      setEmailing(false);
    }
  };

  const generatedAt = overview?.generated_at
    ? new Date(overview.generated_at)
    : null;

  return (
    <div data-testid="admin-analytics" className="space-y-7">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Director console</p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">Analytics</h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-600">
            What's happening today, what's trending this week, and what needs your attention next.
          </p>
          {generatedAt && (
            <p
              data-testid="admin-analytics-generated-at"
              className="mt-1 text-[11px] text-neutral-400"
            >
              Generated {generatedAt.toLocaleString(undefined, {
                month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
              })}
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="admin-analytics-email-summary"
            onClick={onEmailSummary}
            disabled={emailing || !overview}
            title="Email this summary to your inbox"
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60"
          >
            <PaperPlaneTilt size={12} weight="duotone" />
            {emailing ? "Sending…" : "Email me this"}
          </button>
          <button
            type="button"
            data-testid="admin-analytics-refresh"
            onClick={refresh}
            title={generatedAt ? `Last refreshed ${generatedAt.toLocaleString()}` : "Refresh"}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {/* A. Action needed today — reuses the morning brief widget. */}
      <section data-testid="admin-analytics-today">
        <SectionHeader
          icon={Sparkle}
          eyebrow="Today"
          title="Action needed"
          subtitle="Live signals from the morning brief."
        />
        <DirectorMorningBrief />
      </section>

      {loading && !overview && (
        <p className="text-sm text-neutral-500">Loading…</p>
      )}

      {/* B. Trends — week-over-week deltas, no chart library. */}
      {overview && (
        <TrendSection overview={overview} />
      )}

      {/* C. Risk / exception spotting */}
      {overview && (
        <RiskSection overview={overview} risks={risks} />
      )}

      {/* D. Drill-down quick links */}
      <section data-testid="admin-analytics-quicklinks">
        <SectionHeader
          icon={Buildings}
          eyebrow="Drill in"
          title="Operational pages"
          subtitle="Open the page behind any number above."
        />
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          <QuickLink to="/admin/attendance"  label="Attendance"  icon={Users} testId="admin-analytics-link-attendance" />
          <QuickLink to="/admin/billing"     label="Billing"     icon={Receipt} testId="admin-analytics-link-billing" />
          <QuickLink to="/admin/incidents"   label="Incidents"   icon={FirstAid} testId="admin-analytics-link-incidents" />
          <QuickLink to="/admin/compliance"  label="Compliance"  icon={ShieldCheck} testId="admin-analytics-link-compliance" />
          <QuickLink to="/admin/conferences" label="Conferences" icon={CalendarCheck} testId="admin-analytics-link-conferences" />
          <QuickLink to="/admin/inquiries"   label="Inquiries"   icon={Buildings} testId="admin-analytics-link-inquiries" />
        </ul>
      </section>
    </div>
  );
}

function SectionHeader({ icon: Icon, eyebrow, title, subtitle }) {
  return (
    <header className="mb-3">
      <div className="flex items-center gap-2">
        {Icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
            <Icon size={14} weight="duotone" />
          </span>
        )}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">{eyebrow}</p>
          <p className="text-base font-semibold text-neutral-900">{title}</p>
        </div>
      </div>
      {subtitle && <p className="mt-1 text-[12px] text-neutral-500">{subtitle}</p>}
    </header>
  );
}

function TrendSection({ overview }) {
  const a = overview.attendance || {};
  const i = overview.incidents || {};
  const b = overview.billing || {};
  const sc = overview.staff_compliance || {};
  const cc = overview.child_compliance || {};

  const attDelta = deltaArrow(a.present_this_week, a.present_last_week);
  const incDelta = deltaArrow(i.this_week, i.last_week);

  return (
    <section data-testid="admin-analytics-trends">
      <SectionHeader
        icon={ArrowUpRight}
        eyebrow="This week"
        title="Trends at a glance"
        subtitle="Week-over-week deltas across the streams that matter most."
      />
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <TrendCard
          testId="admin-analytics-trend-attendance"
          icon={Users}
          label="Avg children present"
          value={(a.enrolled ?? 0) < 5 ? "—" : (a.present_this_week ?? 0)}
          sub={
            (a.enrolled ?? 0) < 5
              ? `Low sample · only ${a.enrolled ?? 0} enrolled`
              : a.attendance_rate_today != null
                ? `${a.attendance_rate_today}% today · ${a.enrolled} enrolled`
                : `${a.enrolled} enrolled`
          }
          delta={(a.enrolled ?? 0) < 5 ? null : attDelta}
          tone={(a.enrolled ?? 0) < 5 ? "neutral" : deltaTone(attDelta?.dir, false)}
          to="/admin/attendance"
        />
        <TrendCard
          testId="admin-analytics-trend-incidents"
          icon={FirstAid}
          label="Incidents this week"
          value={i.this_week ?? 0}
          sub={`${i.unacked_total ?? 0} awaiting parent ack`}
          delta={incDelta}
          tone={deltaTone(incDelta?.dir, true)}
          to="/admin/incidents"
        />
        <TrendCard
          testId="admin-analytics-trend-billing"
          icon={Receipt}
          label="Open balance"
          value={centsToUsd(b.open_balance_cents || 0)}
          sub={`${b.open_invoice_count ?? 0} open invoices · ${b.aged_30d_count ?? 0} aged 30d+`}
          delta={null}
          tone={(b.aged_30d_count ?? 0) > 0 ? "warn" : "neutral"}
          to="/admin/billing"
          isMoney
        />
        <TrendCard
          testId="admin-analytics-trend-staff-comp"
          icon={ShieldCheck}
          label="Staff certs expiring"
          value={(sc.expiring_soon ?? 0) + (sc.expired ?? 0)}
          sub={`${sc.expired ?? 0} expired · ${sc.expiring_soon ?? 0} within 30d`}
          delta={null}
          tone={(sc.expired ?? 0) > 0 ? "alert" : (sc.expiring_soon ?? 0) > 0 ? "warn" : "ok"}
          to="/admin/compliance"
        />
        <TrendCard
          testId="admin-analytics-trend-child-comp"
          icon={ShieldCheck}
          label="Child records expiring"
          value={(cc.total ?? 0) < 5 ? "—" : ((cc.expiring_soon ?? 0) + (cc.expired ?? 0))}
          sub={
            (cc.total ?? 0) < 5
              ? `Low sample · only ${cc.total ?? 0} on file`
              : `${cc.expired ?? 0} overdue · ${cc.expiring_soon ?? 0} within 30d`
          }
          delta={null}
          tone={
            (cc.total ?? 0) < 5
              ? "neutral"
              : (cc.expired ?? 0) > 0
                ? "alert"
                : (cc.expiring_soon ?? 0) > 0
                  ? "warn"
                  : "ok"
          }
          to="/admin/compliance"
        />
        <TrendCard
          testId="admin-analytics-trend-conferences"
          icon={CalendarCheck}
          label="Conferences next 7 days"
          value={overview.conferences?.booked_next_7d ?? 0}
          sub={`${overview.conferences?.open_next_7d ?? 0} open slots`}
          delta={null}
          tone="neutral"
          to="/admin/conferences"
        />
        <TrendCard
          testId="admin-analytics-trend-autopay-runs"
          icon={Receipt}
          label="Autopay this week"
          value={overview.billing?.autopay_succeeded_7d ?? 0}
          sub={
            (overview.billing?.autopay_attempted_7d ?? 0) === 0
              ? "No attempts in the last 7 days"
              : `${overview.billing?.autopay_failed_7d ?? 0} failed · ${overview.billing?.autopay_attempted_7d ?? 0} attempted`
          }
          delta={null}
          tone={(overview.billing?.autopay_failed_7d ?? 0) > 0 ? "warn" : "ok"}
          to="/admin/billing"
        />
      </ul>
    </section>
  );
}

function TrendCard({ testId, icon: Icon, label, value, sub, delta, tone, to, isMoney }) {
  const toneClass = {
    ok:    "border-emerald-200",
    warn:  "border-amber-300",
    alert: "border-[#C6851E]/40",
  }[tone || ""] || "border-neutral-200";

  return (
    <li>
      <Link
        to={to}
        data-testid={testId}
        className={`block rounded-2xl border bg-white p-4 transition-all hover:-translate-y-[1px] hover:shadow-md ${toneClass}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
            <Icon size={14} weight="duotone" />
          </span>
          {delta && <DeltaBadge delta={delta} tone={tone} />}
        </div>
        <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
        <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{isMoney ? value : String(value)}</p>
        {sub && <p className="mt-1 text-[11px] text-neutral-500">{sub}</p>}
      </Link>
    </li>
  );
}

function DeltaBadge({ delta, tone }) {
  if (!delta) return null;
  const cls = tone === "ok" ? "text-emerald-700"
            : tone === "warn" ? "text-amber-800"
            : tone === "alert" ? "text-[#8A5A12]"
            : "text-neutral-500";
  const Icon = delta.dir === "up" ? ArrowUpRight : delta.dir === "down" ? ArrowDownRight : MinusCircle;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${cls}`}>
      <Icon size={11} weight="bold" />
      <span className="tabular-nums">{delta.text}</span>
    </span>
  );
}

function RiskSection({ overview, risks }) {
  const inc = overview.incidents || {};
  const b   = overview.billing || {};
  const cl  = overview.clearance || {};
  const iq  = overview.inquiries || {};

  const ratioBreaches = useMemo(() => {
    const set = new Set();
    for (const r of risks.ratios || []) {
      if (r.ratioBreach || r.certBreach) set.add(r.classroomId);
    }
    return set.size;
  }, [risks.ratios]);

  const items = [
    {
      key: "blocked",
      tone: cl.children_blocked > 0 ? "alert" : "ok",
      icon: Warning,
      label: "Children blocked at drop-off",
      value: cl.children_blocked ?? 0,
      sub: cl.children_blocked > 0 ? `${cl.total_blockers} total blockers` : "All cleared",
      to: "/admin/compliance",
      testId: "admin-analytics-risk-blocked",
    },
    {
      key: "incidents-unacked",
      tone: inc.unacked_total > 0 ? "warn" : "ok",
      icon: FirstAid,
      label: "Unacknowledged incident reports",
      value: inc.unacked_total ?? 0,
      sub: inc.unacked_total > 0 ? "Parent ack overdue" : "Up to date",
      to: "/admin/incidents",
      testId: "admin-analytics-risk-incidents",
    },
    {
      key: "billing-aged",
      tone: b.aged_30d_count > 0 ? "warn" : "ok",
      icon: Receipt,
      label: "Invoices aged 30+ days",
      value: b.aged_30d_count ?? 0,
      sub: b.failed_invoice_count > 0 ? `${b.failed_invoice_count} failed payments` : "—",
      to: "/admin/billing",
      testId: "admin-analytics-risk-aged",
    },
    {
      key: "ratios",
      tone: ratioBreaches > 0 ? "warn" : "ok",
      icon: Buildings,
      label: "Classrooms at staffing risk",
      value: ratioBreaches,
      sub: ratioBreaches > 0 ? "Over-ratio or cert gap this week" : "Within ratio + certified",
      to: "/admin/compliance",
      testId: "admin-analytics-risk-ratios",
    },
    {
      key: "inquiries-aged",
      tone: iq.aged_7d > 0 ? "warn" : "ok",
      icon: Buildings,
      label: "Open inquiries > 7 days",
      value: iq.aged_7d ?? 0,
      sub: `${iq.open_total ?? 0} open total`,
      to: "/admin/inquiries",
      testId: "admin-analytics-risk-inquiries",
    },
    {
      key: "autopay",
      tone: (b.autopay_total ?? 0) < 5
        ? "neutral"
        : (b.autopay_coverage_pct != null && b.autopay_coverage_pct < 75) ? "warn" : "ok",
      icon: Receipt,
      label: "Autopay coverage",
      value: (b.autopay_total ?? 0) < 5
        ? "—"
        : (b.autopay_coverage_pct != null ? `${b.autopay_coverage_pct}%` : "—"),
      sub: (b.autopay_total ?? 0) < 5
        ? `Low sample · only ${b.autopay_total ?? 0} famil${(b.autopay_total ?? 0) === 1 ? "y" : "ies"}`
        : `${b.autopay_with_pm ?? 0} of ${b.autopay_total ?? 0} families on autopay`,
      to: "/admin/billing",
      testId: "admin-analytics-risk-autopay",
    },
    {
      key: "autopay-eligible",
      tone: (b.autopay_eligible_count ?? 0) > 0 ? "ok" : "neutral",
      icon: Lightning,
      label: "Next autopay run",
      value: b.autopay_eligible_count ?? 0,
      sub: (b.autopay_eligible_count ?? 0) > 0
        ? `${centsToUsd(b.autopay_eligible_balance_cents || 0)} ready to charge`
        : "Nothing eligible right now",
      to: "/admin/billing",
      testId: "admin-analytics-risk-autopay-eligible",
    },
    {
      // Module F · how many families have completed at least step-1
      // of Stripe onboarding (have a stripe_customer_id). This is a
      // softer, earlier-funnel signal than autopay coverage.
      key: "family-activation",
      tone: (b.autopay_total ?? 0) < 5
        ? "neutral"
        : (b.family_activation_pct != null && b.family_activation_pct < 50)
          ? "warn"
          : (b.family_activation_pct != null && b.family_activation_pct >= 80)
            ? "ok"
            : "neutral",
      icon: UserCheck,
      label: "Family activation",
      value: (b.autopay_total ?? 0) < 5
        ? "—"
        : (b.family_activation_pct != null ? `${b.family_activation_pct}%` : "—"),
      sub: (b.autopay_total ?? 0) < 5
        ? `Low sample · only ${b.autopay_total ?? 0} famil${(b.autopay_total ?? 0) === 1 ? "y" : "ies"}`
        : `${b.autopay_with_customer ?? 0} of ${b.autopay_total ?? 0} families set up in Stripe`,
      to: "/admin/billing",
      testId: "admin-analytics-risk-family-activation",
    },
  ];

  return (
    <section data-testid="admin-analytics-risks">
      <SectionHeader
        icon={ShieldCheck}
        eyebrow="Risk"
        title="What needs attention"
        subtitle="Calm summary — green is good, yellow needs follow-up, red blocks the day."
      />
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => {
          const { key, ...rest } = it;
          return <RiskCard key={key} {...rest} />;
        })}
      </ul>
    </section>
  );
}

function RiskCard({ testId, tone, icon: Icon, label, value, sub, to }) {
  const toneClass = {
    ok:    "border-emerald-200 bg-emerald-50/40",
    warn:  "border-amber-300 bg-amber-50/40",
    alert: "border-[#C6851E]/40 bg-[#C6851E]/5",
  }[tone || ""] || "border-neutral-200 bg-white";

  return (
    <li>
      <Link
        to={to}
        data-testid={testId}
        data-tone={tone}
        className={`block rounded-2xl border p-3 transition-all hover:-translate-y-[1px] hover:shadow-sm ${toneClass}`}
      >
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-neutral-700">
            <Icon size={16} weight="duotone" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
            <p className="mt-0.5 font-heading text-xl font-semibold text-neutral-900">{String(value)}</p>
            {sub && <p className="mt-0.5 text-[11px] text-neutral-600">{sub}</p>}
          </div>
        </div>
      </Link>
    </li>
  );
}

function QuickLink({ to, label, icon: Icon, testId }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testId}
        className="flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white p-3 text-sm font-semibold text-neutral-800 transition-all hover:-translate-y-[1px] hover:shadow-sm"
      >
        <Icon size={14} weight="duotone" className="text-primary-hover" />
        {label}
      </Link>
    </li>
  );
}
